import React, { FC, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { overrides } from '@cld/button';
import { Icon } from '@cld/icon';
import { CodeTabSectionProps, ButtonsWrapper, ExpandButton } from '@cld/widget-demos-common';
import { Section } from '../../common/Section';
import { messages } from '../../../../i18n';
import { useConsoleResponse } from '../../../../selectors';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { ConsoleResponseState } from '../../../../types';
import { clearResponse } from '../../../../actions';
import { HalfHeightCodeWrapper } from './common/HalfHeightCodeWrapper';

const { Button } = overrides.mediaLibraryWidgetDemoOverrides;

const stringifyConsoleResponse = (consoleResponse: ConsoleResponseState = []) => consoleResponse
  .map((asset) => JSON.stringify(asset, null, 2))
  .join(',\n');

export const ConsoleResponse: FC<CodeTabSectionProps> = ({ isExpanded, onExpandToggle }) => {
  const analytics = useAnalytics();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const consoleResponse = useConsoleResponse();

  const handleExpandButtonClick = useCallback(() => {
    const action = !isExpanded ? 'expand' : 'collapse';
    analytics(action, `${action} console response`);
    onExpandToggle();
  }, [isExpanded, analytics, onExpandToggle]);

  const handleClearResponse = useCallback(() => {
    analytics('remove', 'clear console code');
    dispatch(clearResponse());
  }, [analytics, dispatch]);

  return (
    <Section title={formatMessage(messages['demo.codeSection.consoleResponse.title'])}>
      <HalfHeightCodeWrapper isExpanded={isExpanded}>
        {stringifyConsoleResponse(consoleResponse)}
      </HalfHeightCodeWrapper>
      <ButtonsWrapper>
        <Button size="sm" onClick={handleClearResponse} data-test="clear-response">
          <Icon name="del" />
          <FormattedMessage {...messages['demo.codeSection.consoleResponse.clearResponse']} />
        </Button>
        <ExpandButton isSectionExpanded={isExpanded} onClick={handleExpandButtonClick} data-test="expand-response" />
      </ButtonsWrapper>
    </Section>
  );
};
